import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Stack,
  Alert,
  Divider,
  Chip,
  InputAdornment,
  Paper,
} from "@mui/material";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import { createPatient } from "../api/patientsApi";
import { prettyError } from "../utils/prettyError";

// ✅ use your Fludee Colors (adjust relative path if needed)
import { Colors } from "../../utils/Colors";

const SEX_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
  { value: "UNKNOWN", label: "Unknown" },
];

// ----- validators (match Django validators) -----
const isAadhaar = (s) => s === "" || /^[0-9]{12}$/.test(String(s || ""));
const isPhone = (s) => s === "" || /^\+?[0-9]{7,15}$/.test(String(s || ""));
const isPositiveIntOrEmpty = (v) => v === "" || (/^[0-9]+$/.test(String(v)) && Number(v) >= 0);

const initial = {
  full_name: "",
  age_years: "",
  dob: "",
  sex: "",

  primary_phone: "",
  address: "",
  aadhaar_card_number: "",
  alternate_contact_number: "",

  emergency_contact_name: "",
  emergency_relationship: "",
  emergency_contact_number: "",
};

// ---------- shared field UI ----------
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    background: Colors.bg.paper,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: Colors.border.input,
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: Colors.text.sub,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: Colors.brand.primary,
    borderWidth: "2px",
  },
  "& .MuiInputBase-input": {
    color: Colors.text.body,
  },
  "& .MuiFormLabel-root": {
    color: Colors.text.muted,
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: Colors.brand.primary,
  },
  "& .MuiFormHelperText-root": {
    color: Colors.text.sub,
  },
};

function Section({ title, icon, children }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: Colors.text.title }}>
          {title}
        </Typography>
      </Stack>
      <Divider sx={{ mb: 2, borderColor: Colors.border.topLine }} />
      {children}
    </Box>
  );
}

export default function PatientCreateForm() {
  const [form, setForm] = React.useState(initial);
  const [touched, setTouched] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const onChange = (key) => (e) => {
    setSuccess("");
    setError("");
    setForm((p) => ({ ...p, [key]: e.target.value }));
    setTouched((p) => ({ ...p, [key]: true }));
  };

  const onBlur = (key) => () => setTouched((p) => ({ ...p, [key]: true }));

  const fieldErrors = React.useMemo(() => {
    const fe = {};
    const show = (key) => !!touched[key] || submitted;

    if (show("full_name")) {
      if (!form.full_name.trim()) fe.full_name = "Full name is required";
    }

    if (show("age_years")) {
      if (!isPositiveIntOrEmpty(form.age_years)) fe.age_years = "Age must be a positive number";
    }

    if (show("primary_phone")) {
      if (!isPhone(form.primary_phone)) fe.primary_phone = "Phone must be 7–15 digits, optional +";
    }

    if (show("aadhaar_card_number")) {
      if (!isAadhaar(form.aadhaar_card_number)) fe.aadhaar_card_number = "Aadhaar must be exactly 12 digits";
    }

    if (show("alternate_contact_number")) {
      if (!isPhone(form.alternate_contact_number)) {
        fe.alternate_contact_number = "Alternate phone must be 7–15 digits, optional +";
      }
    }

    if (show("emergency_contact_number")) {
      if (!isPhone(form.emergency_contact_number)) {
        fe.emergency_contact_number = "Emergency phone must be 7–15 digits, optional +";
      }
    }

    return fe;
  }, [form, touched, submitted]);

  const hasAnyFrontendError = Object.keys(fieldErrors).length > 0;

  const submit = async () => {
    setSubmitted(true);
    setError("");
    setSuccess("");

    if (hasAnyFrontendError) {
      setError("Please fix the highlighted fields.");
      return;
    }

    const alternate_contact =
      form.alternate_contact_number?.trim()
        ? { phone: form.alternate_contact_number.trim() }
        : null;

    const payload = {
      full_name: form.full_name.trim(),
      age_years: form.age_years === "" ? null : Number(form.age_years),
      dob: form.dob || null,
      sex: form.sex || null,

      primary_phone: form.primary_phone || null,
      address: form.address || "",

      aadhaar_card_number: form.aadhaar_card_number || null,
      alternate_contact,

      emergency_contact_name: form.emergency_contact_name || null,
      emergency_relationship: form.emergency_relationship || null,
      emergency_contact_number: form.emergency_contact_number || null,
    };

    setLoading(true);
    try {
      const created = await createPatient(payload);

      setSuccess(
        `Patient created: ${created?.full_name || payload.full_name} (UHID: ${created?.uhid || "generated"})`
      );

      setForm(initial);
      setTouched({});
      setSubmitted(false);
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setError("");
    setSuccess("");
    setForm(initial);
    setTouched({});
    setSubmitted(false);
  };

  const okState = (key) => !fieldErrors[key] && (touched[key] || submitted);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: Colors.bg.app, minHeight: "100%" }}>
      <Box sx={{ maxWidth: 1100, mx: "auto" }}>
        {/* Header */}
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 900, color: Colors.text.title }}>
              Create Patient Uhid
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.text.muted }}>
              UHID is auto-generated by server (DDMMYYYY + 6-digit serial)
            </Typography>
            <Box sx={{ height: 6, mt: 1, borderRadius: 2, background: Colors.brand.accentBar, width: 220 }} />
          </Box>

          <Stack direction="row" spacing={1} sx={{ display: { xs: "none", sm: "flex" } }}>
            <Chip
              label="UHID Auto"
              variant="outlined"
              sx={{ borderColor: Colors.border.input, color: Colors.text.body }}
            />
            <Chip
              icon={<LocalPhoneOutlinedIcon />}
              label="Phone validation"
              variant="outlined"
              sx={{ borderColor: Colors.border.input, color: Colors.text.body }}
            />
            <Chip
              icon={<CreditCardOutlinedIcon />}
              label="Aadhaar optional"
              variant="outlined"
              sx={{ borderColor: Colors.border.input, color: Colors.text.body }}
            />
          </Stack>
        </Stack>

        <Card
          elevation={0}
          sx={{
            border: `1px solid ${Colors.border.light}`,
            borderRadius: 3,
            background: Colors.bg.paper,
            boxShadow: Colors.shadow.brand,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack spacing={2}>
              {!!error && (
                <Alert severity="error" icon={<ReportProblemOutlinedIcon />} sx={{ whiteSpace: "pre-line" }}>
                  {error}
                </Alert>
              )}
              {!!success && (
                <Alert severity="success" sx={{ "& .MuiAlert-icon": { color: Colors.status.success } }}>
                  {success}
                </Alert>
              )}

              {/* Patient Details */}
              <Section title="Patient Details" icon={<PersonOutlineIcon fontSize="small" />}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Full Name *"
                      value={form.full_name}
                      onChange={onChange("full_name")}
                      onBlur={onBlur("full_name")}
                      fullWidth
                      placeholder="Patient name"
                      error={!!fieldErrors.full_name}
                      helperText={fieldErrors.full_name || "As per patient / ID card"}
                      sx={fieldSx}
                      color={okState("full_name") ? "success" : "primary"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Age (years)"
                      value={form.age_years}
                      onChange={onChange("age_years")}
                      onBlur={onBlur("age_years")}
                      fullWidth
                      placeholder="e.g. 45"
                      error={!!fieldErrors.age_years}
                      helperText={fieldErrors.age_years || "Optional (can use DOB instead)"}
                      inputProps={{ inputMode: "numeric" }}
                      sx={fieldSx}
                      color={okState("age_years") ? "success" : "primary"}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="DOB"
                      type="date"
                      value={form.dob}
                      onChange={onChange("dob")}
                      onBlur={onBlur("dob")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText="Optional"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      label="Sex"
                      value={form.sex}
                      onChange={onChange("sex")}
                      onBlur={onBlur("sex")}
                      fullWidth
                      select
                      helperText="Optional"
                      sx={fieldSx}
                    >
                      <MenuItem value="">—</MenuItem>
                      {SEX_OPTIONS.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Section>

              {/* Contact */}
              <Section title="Contact" icon={<LocalPhoneOutlinedIcon fontSize="small" />}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Primary Phone"
                      value={form.primary_phone}
                      onChange={onChange("primary_phone")}
                      onBlur={onBlur("primary_phone")}
                      fullWidth
                      placeholder="+9199xxxxxx or 99xxxxxx"
                      error={!!fieldErrors.primary_phone}
                      helperText={fieldErrors.primary_phone || "7–15 digits, optional leading +"}
                      sx={fieldSx}
                      color={okState("primary_phone") ? "success" : "primary"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalPhoneOutlinedIcon fontSize="small" sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Alternate Contact Number"
                      value={form.alternate_contact_number}
                      onChange={onChange("alternate_contact_number")}
                      onBlur={onBlur("alternate_contact_number")}
                      fullWidth
                      placeholder="+9199xxxxxx or 99xxxxxx"
                      error={!!fieldErrors.alternate_contact_number}
                      helperText={fieldErrors.alternate_contact_number || "Optional"}
                      sx={fieldSx}
                      color={okState("alternate_contact_number") ? "success" : "primary"}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocalPhoneOutlinedIcon fontSize="small" sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      label="Aadhaar Card Number"
                      value={form.aadhaar_card_number}
                      onChange={onChange("aadhaar_card_number")}
                      onBlur={onBlur("aadhaar_card_number")}
                      fullWidth
                      placeholder="12 digits"
                      error={!!fieldErrors.aadhaar_card_number}
                      helperText={fieldErrors.aadhaar_card_number || "Optional (12 digits)"}
                      sx={fieldSx}
                      color={okState("aadhaar_card_number") ? "success" : "primary"}
                      inputProps={{ inputMode: "numeric", maxLength: 12 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCardOutlinedIcon fontSize="small" sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Address"
                      value={form.address}
                      onChange={onChange("address")}
                      onBlur={onBlur("address")}
                      fullWidth
                      placeholder="House / Street / City"
                      helperText="Optional"
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* Emergency Contact */}
              <Section title="Emergency Contact" icon={<PersonOutlineIcon fontSize="small" />}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Emergency Contact Name"
                      value={form.emergency_contact_name}
                      onChange={onChange("emergency_contact_name")}
                      onBlur={onBlur("emergency_contact_name")}
                      fullWidth
                      helperText="Optional"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Relationship"
                      value={form.emergency_relationship}
                      onChange={onChange("emergency_relationship")}
                      onBlur={onBlur("emergency_relationship")}
                      fullWidth
                      helperText="Optional"
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Emergency Phone"
                      value={form.emergency_contact_number}
                      onChange={onChange("emergency_contact_number")}
                      onBlur={onBlur("emergency_contact_number")}
                      fullWidth
                      placeholder="+9199xxxxxx or 99xxxxxx"
                      error={!!fieldErrors.emergency_contact_number}
                      helperText={fieldErrors.emergency_contact_number || "Optional, but recommended"}
                      sx={fieldSx}
                      color={okState("emergency_contact_number") ? "success" : "primary"}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* Actions */}
              <Divider sx={{ my: 1, borderColor: Colors.border.topLine }} />
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button
                  onClick={reset}
                  disabled={loading}
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: Colors.border.input,
                    color: Colors.text.body,
                    "&:hover": { borderColor: Colors.brand.primary, background: Colors.bg.hover },
                  }}
                >
                  RESET
                </Button>

                <Button
                  onClick={submit}
                  disabled={loading}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 900,
                    backgroundColor: Colors.brand.primary,
                    "&:hover": { backgroundColor: Colors.brand.primaryDark },
                  }}
                >
                  {loading ? "SAVING..." : "CREATE PATIENT"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}